import type { Campaign, EntityTag } from '~/types/Candidates';

/**
 * Label for a campaign's "led by" pill, from the best signal available.
 *
 * 1. `leadSource` — the candidate and party profiles nest campaigns without
 *    their relations, so they resolve the lead from the response shape instead
 *    (a campaign inherited via a party is party-led; every campaign on a party
 *    profile is party-led) and pass the answer down. It is the most specific
 *    signal, so it wins.
 * 2. `tags` — the attached candidates and parties. A campaign can have both, in
 *    which case the design merges the two into one "Party and Candidate Led"
 *    pill rather than showing a pill per side (Figma `41765:24397`).
 * 3. `type` — the campaign's own classification. This is a last resort: it is a
 *    single value that can only ever name one side, and it describes the
 *    campaign rather than who leads it, so it is only right where there is
 *    nothing else to go on (the campaigns listing and the campaign detail page).
 */
// Readonly-friendly: useCampaign returns a `readonly()` ref, and a mutable
// Campaign is assignable to this shape too, so both call sites work.
interface CampaignTypeSource {
  type: Campaign['type'];
  tags: readonly Pick<EntityTag, 'type'>[];
  leadSource?: Campaign['leadSource'];
}

export function campaignTypeLabel(campaign: CampaignTypeSource): string {
  if (campaign.leadSource === 'party') return 'Party Led';
  if (campaign.leadSource === 'candidate') return 'Candidate Led';

  const hasCandidates = campaign.tags.some((tag) => tag.type === 'candidate');
  const hasParties = campaign.tags.some((tag) => tag.type === 'party');

  if (hasCandidates && hasParties) return 'Party and Candidate Led';
  if (hasParties) return 'Party Led';
  if (hasCandidates) return 'Candidate Led';

  return campaign.type === 'community' ? 'Candidate Led' : 'Party Led';
}
